import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles,
  CalendarHeart,
  Crown,
  Headphones,
  Star,
  ArrowRight,
  Phone,
  Instagram,
  Twitter,
  Facebook,
  Linkedin,
  Music2,
  Ghost,
  Mail,
} from "lucide-react";

const SOCIALS = [
  { name: "Instagram", url: "https://instagram.com/fallenangelsny", Icon: Instagram },
  { name: "X", url: "https://x.com/fallenangelsny", Icon: Twitter },
  { name: "Facebook", url: "https://facebook.com/fallenangelsny", Icon: Facebook },
  { name: "TikTok", url: "https://tiktok.com/@fallenangelsny", Icon: Music2 },
  { name: "Snapchat", url: "https://snapchat.com/add/fallenangelsny", Icon: Ghost },
  { name: "LinkedIn", url: "https://www.linkedin.com/company/fallenangelsny", Icon: Linkedin },
];

const CONTACT_EMAILS = ["guardianangels@fallenangelsny.com", "jc@fallenangelsny.com"];
import ChatWidget from "../components/ChatWidget";
import BookingDialog from "../components/BookingDialog";
import ApplicantDialog from "../components/ApplicantDialog";

const HERO_IMG =
  "https://images.unsplash.com/photo-1592494804071-faea15d93a8a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBuaWdodGxpZmUlMjBsb3VuZ2V8ZW58MHx8fHwxNzg2NjkzMjYxfDA&ixlib=rb-4.1.0&q=85";
const MODEL_IMG =
  "https://images.unsplash.com/photo-1664076458686-3449062080ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxnbGFtb3JvdXMlMjBmYXNoaW9uJTIwbW9kZWx8ZW58MHx8fHwxNzg2NjkzMjYxfDA&ixlib=rb-4.1.0&q=85";

const AgeGate = ({ onEnter }) => (
  <div className="fixed inset-0 z-50 bg-[#0A0A0A] flex items-center justify-center px-6" data-testid="age-gate">
    <div className="max-w-md text-center fade-up">
      <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Fallen Angels Entertainment</p>
      <h1 className="font-display text-5xl font-light mb-6">Age Verification</h1>
      <p className="text-[#A3A3A3] mb-8 leading-relaxed">
        This experience contains adult content. You must be 18 years or older to enter and speak with your Guardian Angel.
      </p>
      <div className="flex flex-col gap-3">
        <button
          onClick={onEnter}
          className="bg-[#D4AF37] text-[#0A0A0A] font-semibold py-3 rounded-sm hover:bg-[#F3E5AB] transition-colors"
          data-testid="age-gate-enter"
        >
          I am 18 or older — Enter
        </button>
        <a href="https://www.google.com" className="text-[#A3A3A3] text-sm py-2 hover:text-[#F5F5F5] transition-colors">
          I am under 18 — Exit
        </a>
      </div>
    </div>
  </div>
);

const services = [
  { icon: Crown, title: "Heaven on Earth", desc: "Private, premium appointments with our exquisite Fallen Angel Models — discreet and unforgettable." },
  { icon: CalendarHeart, title: "Special Events", desc: "Galas, parties and private functions elevated by the presence of our angels." },
  { icon: Headphones, title: "Concierge & Tech Support", desc: "Seraphina assists with memberships, the website and any question, day or night." },
  { icon: Star, title: "Become an Angel", desc: "Aspiring models are pre-screened by Seraphina and invited to join our elite roster." },
];

export default function Landing() {
  const [entered, setEntered] = useState(false);
  const [booking, setBooking] = useState(null); // "special_event" | "heaven_on_earth" | null
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("enter") === "1") {
        setEntered(true);
        return;
      }
      if (sessionStorage.getItem("fa_age_ok")) setEntered(true);
    } catch {
      /* sessionStorage unavailable (private mode) — age gate will show */
    }
  }, []);

  const enter = () => {
    setEntered(true);
    try {
      sessionStorage.setItem("fa_age_ok", "1");
    } catch {
      /* sessionStorage unavailable (private mode) */
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] relative">
      {!entered && <AgeGate onEnter={enter} />}

      {/* Nav */}
      <nav className="fixed top-0 inset-x-0 z-40 backdrop-blur-xl bg-[#0A0A0A]/60 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="text-[#D4AF37]" size={20} />
            <span className="font-display text-xl tracking-wide">Fallen Angels</span>
          </div>
          <div className="flex items-center gap-6">
            <a href="tel:+13159560685" className="hidden md:flex items-center gap-2 text-sm text-[#D4AF37] hover:text-[#F3E5AB] transition-colors" data-testid="nav-phone">
              <Phone size={15} /> 315-956-0685
            </a>
            <a href="#services" className="hidden sm:block text-sm text-[#A3A3A3] hover:text-[#D4AF37] transition-colors">Services</a>
            <a href="#reception" className="hidden sm:block text-sm text-[#A3A3A3] hover:text-[#D4AF37] transition-colors">Reception</a>
            <Link
              to="/admin"
              className="text-xs uppercase tracking-[0.18em] text-[#D4AF37] border border-[#D4AF37] rounded-sm px-3 py-1.5 hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors"
              data-testid="nav-admin-link"
            >
              Admin
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center pt-24 pb-16">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/85 to-[#0A0A0A]/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center w-full">
          <div className="fade-up">
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-5">Fallen Angels Entertainment · NYC</p>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-light leading-[1.05] mb-6">
              Meet <span className="italic text-[#D4AF37]">Seraphina</span>, your Guardian Angel.
            </h1>
            <p className="text-lg text-[#A3A3A3] font-light leading-relaxed mb-8 max-w-md">
              Our voice-first AI receptionist welcomes you, answers every question, and arranges your most exquisite evenings — with grace and discretion.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => setBooking("heaven_on_earth")}
                className="bg-[#D4AF37] text-[#0A0A0A] font-semibold px-7 py-3.5 rounded-sm hover:bg-[#F3E5AB] transition-colors flex items-center gap-2"
                data-testid="hero-book-heaven"
              >
                Book Heaven on Earth <ArrowRight size={16} />
              </button>
              <a
                href="#reception"
                className="border border-[#D4AF37] text-[#D4AF37] font-semibold px-7 py-3.5 rounded-sm hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors"
                data-testid="hero-talk-to-seraphina"
              >
                Talk to Seraphina
              </a>
            </div>
            <div className="flex items-center gap-4 mt-8" data-testid="hero-socials">
              <span className="text-xs uppercase tracking-[0.2em] text-[#6b6b6b]">Follow us</span>
              {SOCIALS.map(({ name, url, Icon }) => (
                <a
                  key={name}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={name}
                  className="text-[#A3A3A3] hover:text-[#D4AF37] transition-colors"
                  data-testid={`hero-social-${name.toLowerCase()}`}
                >
                  <Icon size={20} />
                </a>
              ))}
            </div>
          </div>
          <div className="fade-up" style={{ animationDelay: "0.2s" }}>
            <ChatWidget />
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="max-w-7xl mx-auto px-6 py-28">
        <div className="mb-16 max-w-2xl">
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">What we offer</p>
          <h2 className="font-display text-4xl md:text-5xl font-light">
            An experience curated to perfection.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((s, i) => (
            <div
              key={s.title}
              className="bg-[#141414] border border-[#27272A] rounded-lg p-7 hover:-translate-y-1 transition-transform duration-300"
              data-testid={`service-card-${i}`}
            >
              <s.icon className="text-[#D4AF37] mb-5" size={28} />
              <h3 className="font-display text-2xl mb-3">{s.title}</h3>
              <p className="text-sm text-[#A3A3A3] leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Booking split */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-6">
        <div className="relative overflow-hidden rounded-lg border border-[#27272A] p-10 bg-gradient-to-br from-[#5C1A24]/40 to-[#141414]">
          <Crown className="text-[#D4AF37] mb-4" size={32} />
          <h3 className="font-display text-3xl mb-3">Heaven on Earth</h3>
          <p className="text-[#A3A3A3] mb-6 max-w-sm">Reserve a private premium appointment with one of our Fallen Angel Models.</p>
          <button
            onClick={() => setBooking("heaven_on_earth")}
            className="bg-[#D4AF37] text-[#0A0A0A] font-semibold px-6 py-3 rounded-sm hover:bg-[#F3E5AB] transition-colors"
            data-testid="book-heaven-button"
          >
            Reserve Now
          </button>
        </div>
        <div className="relative overflow-hidden rounded-lg border border-[#27272A] p-10 bg-gradient-to-br from-[#141414] to-[#0A0A0A]">
          <CalendarHeart className="text-[#D4AF37] mb-4" size={32} />
          <h3 className="font-display text-3xl mb-3">Special Events</h3>
          <p className="text-[#A3A3A3] mb-6 max-w-sm">Let us bring elegance to your gala, party, or private function.</p>
          <button
            onClick={() => setBooking("special_event")}
            className="border border-[#D4AF37] text-[#D4AF37] font-semibold px-6 py-3 rounded-sm hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors"
            data-testid="book-event-button"
          >
            Plan an Event
          </button>
        </div>
      </section>

      {/* Reception / chat feature */}
      <section id="reception" className="max-w-7xl mx-auto px-6 py-28 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">The Reception Desk</p>
          <h2 className="font-display text-4xl md:text-5xl font-light mb-6">
            Speak, and she listens.
          </h2>
          <p className="text-[#A3A3A3] leading-relaxed mb-4">
            Tap the microphone to talk to Seraphina, or simply type. She'll answer questions about our services, help with tech support, and guide you to the perfect booking — all in her warm, velvet voice.
          </p>
          <p className="text-[#A3A3A3] leading-relaxed">Available 24 / 7, always discreet.</p>
          <a
            href="tel:+13159560685"
            className="inline-flex items-center gap-3 mt-8 bg-[#141414] border border-[#D4AF37] rounded-sm px-6 py-4 hover:bg-[#D4AF37] hover:text-[#0A0A0A] text-[#D4AF37] transition-colors group"
            data-testid="reception-call-link"
          >
            <Phone size={20} />
            <span className="font-display text-2xl">315-956-0685</span>
            <span className="text-xs uppercase tracking-[0.2em] opacity-70">Call Seraphina now</span>
          </a>
        </div>
        <ChatWidget />
      </section>

      {/* Become a model */}
      <section className="relative py-28">
        <div className="absolute inset-0">
          <img src={MODEL_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-l from-[#0A0A0A] via-[#0A0A0A]/85 to-[#0A0A0A]/50" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 flex justify-end">
          <div className="max-w-lg text-right">
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Join the roster</p>
            <h2 className="font-display text-4xl md:text-5xl font-light mb-6">Become a Fallen Angel.</h2>
            <p className="text-[#A3A3A3] leading-relaxed mb-8">
              Seraphina pre-screens all aspiring models. If you have the grace and confidence, we would love to meet you.
            </p>
            <button
              onClick={() => setApplying(true)}
              className="bg-[#D4AF37] text-[#0A0A0A] font-semibold px-7 py-3.5 rounded-sm hover:bg-[#F3E5AB] transition-colors"
              data-testid="apply-model-button"
            >
              Apply to Model
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#27272A] py-12">
        <div className="max-w-7xl mx-auto px-6 flex flex-col gap-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <Sparkles className="text-[#D4AF37]" size={18} />
              <span className="font-display text-lg">Fallen Angels Entertainment Inc.</span>
            </div>
            <div className="flex items-center gap-5" data-testid="footer-socials">
              {SOCIALS.map(({ name, url, Icon }) => (
                <a
                  key={name}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={name}
                  className="text-[#A3A3A3] hover:text-[#D4AF37] transition-colors"
                  data-testid={`footer-social-${name.toLowerCase()}`}
                >
                  <Icon size={20} />
                </a>
              ))}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-6 border-t border-[#1c1c1c]">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
              <a href="tel:+13159560685" className="flex items-center gap-2 text-sm text-[#D4AF37] hover:text-[#F3E5AB] transition-colors" data-testid="footer-phone">
                <Phone size={14} /> 315-956-0685
              </a>
              {CONTACT_EMAILS.map((em) => (
                <a key={em} href={`mailto:${em}`} className="flex items-center gap-2 text-sm text-[#A3A3A3] hover:text-[#D4AF37] transition-colors" data-testid={`footer-email-${em.split("@")[0]}`}>
                  <Mail size={14} /> {em}
                </a>
              ))}
            </div>
            <p className="text-xs text-[#6b6b6b]">Strictly 18+ · Discreet · New York City</p>
          </div>
        </div>
      </footer>

      <BookingDialog open={!!booking} onOpenChange={(o) => !o && setBooking(null)} type={booking} />
      <ApplicantDialog open={applying} onOpenChange={setApplying} />
    </div>
  );
}
